import { useWorkspaceStore } from '@/state/workspaceStore';
import { SKYROVER_COLORS } from '@/theme/skyroverTheme';
import Editor from '@monaco-editor/react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Box, Dialog, IconButton, Snackbar } from '@mui/material';
import { useCallback, useState } from 'react';

const DEFAULT_CODE = `void setup() {\n}\n\nvoid loop() {\n}`;

const EDITOR_OPTIONS = {
  readOnly: true,
  minimap: { enabled: false },
  fontSize: 13,
  fontFamily: '"JetBrains Mono", Consolas, monospace',
  scrollBeyondLastLine: false,
  wordWrap: 'on' as const,
  lineNumbers: 'on' as const,
  renderWhitespace: 'none' as const,
  automaticLayout: true,
  padding: { top: 12 },
  lineDecorationsWidth: 0,
  glyphMargin: false,
  folding: false,
};

const fullscreenButtonSx = {
  bgcolor: '#fff',
  color: SKYROVER_COLORS.header,
  border: `1px solid ${SKYROVER_COLORS.header}`,
  boxShadow: 1,
  '&:hover': {
    bgcolor: SKYROVER_COLORS.header,
    color: '#fff',
  },
} as const;

function CodeEditor({ code }: { code: string }) {
  return (
    <Editor
      height="100%"
      language="cpp"
      theme="light"
      value={code}
      options={EDITOR_OPTIONS}
    />
  );
}

export function CodePreview() {
  const generatedCode = useWorkspaceStore((s) => s.generatedCode);
  const [fullscreen, setFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const code = generatedCode || DEFAULT_CODE;

  const handleCopyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [code]);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#F5F5F5' }}>
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <CodeEditor code={code} />
          <IconButton
            size="small"
            aria-label="Fullscreen code"
            onClick={() => setFullscreen(true)}
            sx={{ position: 'absolute', top: 4, right: 4, ...fullscreenButtonSx }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Dialog fullScreen open={fullscreen} onClose={() => setFullscreen(false)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#F5F5F5' }}>
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <CodeEditor code={code} />
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                display: 'flex',
                gap: 1,
              }}
            >
              <IconButton
                size="small"
                aria-label="Copy code"
                onClick={handleCopyCode}
                sx={fullscreenButtonSx}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Exit fullscreen code"
                onClick={() => setFullscreen(false)}
                sx={fullscreenButtonSx}
              >
                <FullscreenExitIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Code copied to clipboard"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
}
